import { z } from "zod";
import { createIntegrationSchemas } from "@causit/integration-schema";
import type {
  AuthField as SharedAuthField,
  AuthFieldInstance as SharedAuthFieldInstance,
  ConnectorSurfaceKind as SharedConnectorSurfaceKind,
  IntegrationAccess as SharedIntegrationAccess,
  IntegrationAuth as SharedIntegrationAuth,
  IntegrationAuthInstance as SharedIntegrationAuthInstance,
  IntegrationAuthType as SharedIntegrationAuthType,
  IntegrationServer as SharedIntegrationServer,
  IntegrationType as SharedIntegrationType,
  PublicMcpServerConfiguration as SharedPublicMcpServerConfiguration,
  SelectionOption as SharedSelectionOption,
} from "@causit/integration-schema";

const {
  AbilitiesSchema,
  AuthFieldInstanceSchema,
  AuthFieldSchema,
  ConnectorSurfaceKindSchema,
  InstancePolicySchema,
  InstanceScopeSchema,
  IntegrationAccessSchema,
  IntegrationAuthInstanceSchema,
  IntegrationAuthSchema,
  IntegrationAuthTypeSchema,
  IntegrationSandboxSchema,
  IntegrationServerSchema,
  IntegrationTypeSchema,
  PublicMcpServerConfigurationSchema,
  SelectionOptionSchema,
} = createIntegrationSchemas(z);

export {
  AuthFieldInstanceSchema,
  AuthFieldSchema,
  IntegrationAccessSchema,
  IntegrationAuthInstanceSchema,
  IntegrationAuthSchema,
  IntegrationAuthTypeSchema,
  IntegrationSandboxSchema,
  IntegrationServerSchema,
  SelectionOptionSchema,
};

// ── Consolidated from the former dbSchema ────────────────────────────────
// The client has no notion of a DB — there is ONE schema. These are the only
// pieces still needed; the rest of the old db/agent/financial model is dropped.
// NOTE: User keeps its payment/subscription fields even though orphan today —
// intentionally retained for later (do not remove).
export const UserTypeSchema = z.enum(["admin", "manager", "member"]);
export const UserSchema = z.object({
  id: z.string(),
  createdAt: z.string().datetime(),
  lastUpdatedAt: z.string().datetime(),
  subscriptionId: z.string().nullable().default(null),
  subscriptionStatus: z
    .enum(["auto_renewing", "non_renewing", "cancelled", "failed"])
    .nullable()
    .default(null),
  subscriptionAnchor: z.number().nullable().default(null),
  subscriptionEndAt: z.string().datetime().nullable().default(null),
  subscriptionUpdatedAt: z.string().datetime().nullable().default(null),
  paymentCustomerId: z.number().nullable().default(null),
  websocketConnectionIds: z.array(z.string()).default([]),
  websocketLastConnectedAt: z.string().datetime().nullable().default(null),
  assistantTurns: z.number().default(0),
  createdAgents: z.number().default(0),
  forkedAgents: z.number().default(0),
  updatedAgents: z.number().default(0),
  metadata: z.record(z.any()),
  type: UserTypeSchema,
  activeOrgId: z.string().nullable().default(null),
});
export type User = z.infer<typeof UserSchema>;
export type UserType = z.infer<typeof UserTypeSchema>;

export const ActiveOrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentOrgId: z.string().nullable().optional(),
  plan: z.string().optional(),
  orgRole: UserTypeSchema.optional(),
  scopes: z.array(z.any()).optional(),
  managed: z.boolean().optional(),
  inherited: z.boolean().optional(),
}).strict();
export type ActiveOrg = z.infer<typeof ActiveOrgSchema>;

export const ActiveUserOptionSchema = z.object({
  id: z.string(),
  type: UserTypeSchema.optional(),
  activeOrgId: z.string().nullable().optional(),
  name: z.string().optional(),
  fullName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  primaryEmail: z.string().optional(),
  primaryEmailAddress: z.string().optional(),
}).passthrough();
export type ActiveUserOption = z.infer<typeof ActiveUserOptionSchema>;

const ScopedActiveOptionsSchema = <T extends z.ZodTypeAny>(item: T) => z.object({
  ownerId: z.string(),
  items: z.array(item),
  hydratedAt: z.number(),
}).strict();

export const ActiveContextHydrationSchema = z.object({
  fingerprint: z.string().min(1),
  user: UserSchema,
  org: ActiveOrgSchema,
  options: z.object({
    users: ScopedActiveOptionsSchema(ActiveUserOptionSchema),
    orgs: ScopedActiveOptionsSchema(ActiveOrgSchema),
  }).strict(),
}).strict();
export type ActiveContextHydration = z.infer<typeof ActiveContextHydrationSchema>;

export const OrganizationSelectionSchema = z.object({
  user: ActiveUserOptionSchema,
  options: z.object({
    users: ScopedActiveOptionsSchema(ActiveUserOptionSchema),
    orgs: ScopedActiveOptionsSchema(ActiveOrgSchema),
  }).strict(),
}).strict();
export type OrganizationSelection = z.infer<typeof OrganizationSelectionSchema>;

export const StatusSchema = z.enum([
  "running",
  "completed",
  "failed",
  "retrying",
  "completed_with_error",
]);

export const OutletServerConfigSchema = PublicMcpServerConfigurationSchema;
export type OutletServerConfig = SharedPublicMcpServerConfiguration;

export const IntegrationStatusSchema = z.enum([
  "disconnected",
  "connecting",
  "selectionPending",
  "connected",
  "error",
]);

export const SelectOptionRequestSchema = z.object({
  instanceId: z.string(),
  option: SelectionOptionSchema,
});

export const SelectOptionResponseSchema = z.object({
  serverName: z.string(),
  instanceId: z.string(),
  integrationStatus: z.enum(["connected", "failed"]),
  message: z.string().optional(),
});

// Integration definition type. The UI presents the resulting instance surfaces
// as connectors: surface, channel, tool, file, and command (the shared
// @causit/integration-schema declared kind set).
export const ScopeCategorySchema = IntegrationTypeSchema;
export { ConnectorSurfaceKindSchema };
const ConnectorPolicySchema = InstancePolicySchema;
const ConnectorAbilitiesSchema = AbilitiesSchema;
const ConnectorUsageSchema = z.record(z.any());

export const IntegrationServerInstanceSchema = z.object({
  type: ScopeCategorySchema.optional(),
  kind: z.literal("integration").optional(),
  scope: InstanceScopeSchema.optional(),
  orgId: z.string().optional(),
  userId: z.string(),
  displayInfo: z.object({
    title: z.string(),
    subtitle: z.string(),
  }),
  instanceId: z.string(),
  serverName: z.string(),
  instanceName: z.string(),
  status: IntegrationStatusSchema,
  createdAt: z.string().datetime(),
  config: OutletServerConfigSchema,
  additional: z.record(z.any()).optional(),
  pendingOptions: z.array(SelectionOptionSchema).optional(),
  auth: IntegrationAuthInstanceSchema.optional(),
  access: IntegrationAccessSchema.optional(),
  descriptionForAssistant: z.string().optional(),
  policy: ConnectorPolicySchema.optional(),
  usage: ConnectorUsageSchema.optional(),
  expiredAt: z.number().optional(),
  refreshExpiredAt: z.number().optional(),
  sandbox: z.boolean().optional(),
  subscriptionType: z.enum(["webhook", "polling", "custom"]).nullable().optional(),
}).strict();

export const ConnectorSurfaceSchema = z.object({
  surfaceId: z.string(),
  parentInstanceId: z.string(),
  serverName: z.string(),
  kind: ConnectorSurfaceKindSchema,
  scope: InstanceScopeSchema,
  orgId: z.string().optional(),
  userId: z.string().optional(),
  abilities: ConnectorAbilitiesSchema.optional(),
  providerSurfaceId: z.string().optional(),
  providerSurfaceName: z.string().optional(),
  providerSurfaceLabel: z.string().optional(),
  providerSurfaceSchema: z.record(z.any()).optional(),
  providerSurfaceCapabilities: ConnectorAbilitiesSchema.optional(),
  providerSurfaceDescription: z.string().optional(),
  policy: ConnectorPolicySchema.optional(),
  target: z.object({
    kind: z.enum(["human", "causit"]),
    email: z.string().optional(),
  }).optional(),
  auth: IntegrationAuthInstanceSchema.optional(),
  usage: ConnectorUsageSchema.optional(),
  createdAt: z.string().optional(),
  registrationStatus: z.enum(["registered", "unregistered"]).optional(),
  selectable: z.boolean().optional(),
  registeredAt: z.string().optional(),
  registeredBy: z.string().optional(),
  registeredConversationId: z.string().optional(),
  instance: IntegrationServerInstanceSchema,
  server: IntegrationServerSchema,
}).strict();

// THE WORLD-HELD SURFACE CATALOG (the surface cut): one entry = one surface
// SUMMARY synced by the outlet at instance registration and read back through
// the normal query/download door. No schemas ride the catalog (schemas stay
// resolve-on-pick); `providerKind` is the provider's OWN object kind metadata
// (never a gate — every provider object is a surface).
export const ConnectorCatalogSurfaceSchema = z.object({
  surfaceId: z.string(),
  parentInstanceId: z.string(),
  serverName: z.string(),
  kind: ConnectorSurfaceKindSchema,
  scope: InstanceScopeSchema.optional(),
  providerSurfaceId: z.string().optional(),
  providerSurfaceName: z.string().optional(),
  providerSurfaceLabel: z.string().optional(),
  providerSurfaceDescription: z.string().optional(),
  providerKind: z.string().optional(),
  registrationStatus: z.enum(["registered", "unregistered"]),
  selectable: z.boolean(),
}).strict();
export type ConnectorCatalogSurface = z.infer<typeof ConnectorCatalogSurfaceSchema>;

// =============================================================================
// Files (upload processing + finalization)
// =============================================================================

export const TimestampCandidateSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("rowColumn"),
    columnName: z.string(),
    sample: z.string().optional(),
  }),
  z.object({
    source: z.literal("metadata"),
    rowIndex: z.number().int(),
    colIndex: z.number().int(),
    raw: z.string(),
  }),
]);

export const TimestampResolutionSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("resolved"),
  }),
  z.object({
    status: z.literal("pending_selection"),
    candidates: z.array(TimestampCandidateSchema),
  }),
  z.object({
    status: z.literal("pending_input"),
    candidates: z.array(TimestampCandidateSchema).optional(),
  }),
]);

export const FileMetadataSchema = z
  .object({
    status: z.enum(["pending", "available"]),
    timestampResolution: TimestampResolutionSchema.optional(),
  })
  // File metadata contains additional fields server-side (e.g. fileId, fileName, etc.)
  .passthrough();

export const ProcessFileResponseSchema = z
  .object({
    fileMetadata: FileMetadataSchema,
    schemaUpdated: z.boolean(),
    // Optional legacy/extra fields surfaced by the outlet
    message: z.string().optional(),
    bucketName: z.string().optional(),
  })
  .passthrough();

export const FinalizeFileRequestSchema = z.discriminatedUnion("decision", [
  z.object({
    bucketId: z.string(),
    fileId: z.string(),
    decision: z.literal("skip"),
  }),
  z.object({
    bucketId: z.string(),
    fileId: z.string(),
    decision: z.literal("choose"),
    candidateIndex: z.number().int().nonnegative(),
  }),
  z.object({
    bucketId: z.string(),
    fileId: z.string(),
    decision: z.literal("provide"),
    providedTimestamp: z.string(),
  }),
]);

export const FinalizeFileResponseSchema = z
  .object({
    fileMetadata: FileMetadataSchema,
  })
  .passthrough();

export const MCPServerConfigSchema = OutletServerConfigSchema.omit({
  env: true,
}).extend({
  reinstall: z.boolean().optional(),
  // env is encrypted
  env: z.record(z.string(), z.string()).optional(),
});

export const LocationSchema = z.object({
  country: z.string().optional(),
  iso_code: z.string().optional(),
});

export const UserQuotasSchema = z.object({
  remainingTurns: z.number(),
  allowTurns: z.boolean(),
  currentMonth: z.string(),
});

export const AuthResponseSchema = z.object({
  Authorization: z.string(),
  user: UserSchema,
  activeContext: ActiveContextHydrationSchema,
  userQuotas: UserQuotasSchema,
  expiresAt: z.number(),
  location: LocationSchema,
});

// ---- primitives reused by blocks ----

export const PaginationResultSchema = z.object({
  page: z.number(),
  hasMore: z.boolean(),
  items: z.array(z.any()),
});

export const FeedbackRequestSchema = z.object({
  rating: z.number().optional(),
  message: z.string().optional(),
  type: z.string().optional(),
  guestEmail: z.string().optional(),
});

export const FeedbackResponseSchema = z.object({
  message: z.string(),
});

export const ChangeSubscriptionRequestSchema = z.object({
  plan: z.enum(["free", "basic", "pro", "ultra"]),
  redirectUrl: z.string(),
});

export const ChangeSubscriptionCheckoutResponseSchema = z.object({
  responseType: z.literal("checkout"),
  checkoutUrl: z.string(),
  checkoutId: z.string(),
  expiresAt: z.string(),
});

export const ChangeSubscriptionUpdateResponseSchema = z.object({
  responseType: z.literal("update"),
  message: z.string(),
  subscriptionId: z.string(),
  newPlan: z.enum(["free", "basic", "pro", "ultra"]),
  subscriptionAnchor: z.number(),
  updatedAt: z.string(),
});
export const ChangeSubscriptionResponseSchema = z.discriminatedUnion(
  "responseType",
  [
    ChangeSubscriptionCheckoutResponseSchema,
    ChangeSubscriptionUpdateResponseSchema,
  ]
);

// Webhook event types
export const PaymentWebhookEventTypeSchema = z.enum([
  "subscription_created",
  "subscription_resumed",
  "subscription_cancelled",
  "subscription_plan_changed",
  "subscription_payment_failed",
]);

// Webhook request schema
export const PaymentWebhookRequestSchema = z.object({
  meta: z.record(z.any()),
  data: z.record(z.any()), // Raw webhook payload from Lemon Squeezy
});

// Webhook response schema
export const PaymentWebhookResponseSchema = z.object({
  message: z.string(),
});

export type AuthField = SharedAuthField;
export type AuthFieldInstance = SharedAuthFieldInstance;
export type IntegrationAuthType = SharedIntegrationAuthType;
export type IntegrationAuth = SharedIntegrationAuth;
export type IntegrationAuthInstance = SharedIntegrationAuthInstance;
export type IntegrationAccess = SharedIntegrationAccess;
export type ScopeCategory = SharedIntegrationType;
export type ConnectorSurfaceKind = SharedConnectorSurfaceKind;
export type ConnectorSurface = z.infer<typeof ConnectorSurfaceSchema>;
export type FeedbackRequest = z.infer<typeof FeedbackRequestSchema>;
export type UserQuotas = z.infer<typeof UserQuotasSchema>;
export type MCPServerConfig = z.infer<typeof MCPServerConfigSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// Backward compat aliases
/** @deprecated Use GetAgentMetadataInput */
export type Location = z.infer<typeof LocationSchema>;
export type PaginationResult = z.infer<typeof PaginationResultSchema>;
export type IntegrationServerInstance = z.infer<
  typeof IntegrationServerInstanceSchema
>;
export type IntegrationServer = SharedIntegrationServer;
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;
export type SelectionOption = SharedSelectionOption;
export type SelectOptionRequest = z.infer<typeof SelectOptionRequestSchema>;
export type SelectOptionResponse = z.infer<typeof SelectOptionResponseSchema>;
export type TimestampCandidate = z.infer<typeof TimestampCandidateSchema>;
export type TimestampResolution = z.infer<typeof TimestampResolutionSchema>;
export type FileMetadata = z.infer<typeof FileMetadataSchema>;
export type ProcessFileResponse = z.infer<typeof ProcessFileResponseSchema>;
export type FinalizeFileRequest = z.infer<typeof FinalizeFileRequestSchema>;
export type FinalizeFileResponse = z.infer<typeof FinalizeFileResponseSchema>;
export type ChangeSubscriptionRequest = z.infer<
  typeof ChangeSubscriptionRequestSchema
>;
export type ChangeSubscriptionResponse = z.infer<
  typeof ChangeSubscriptionResponseSchema
>;
export type PaymentWebhookEventType = z.infer<
  typeof PaymentWebhookEventTypeSchema
>;
export type PaymentWebhookRequest = z.infer<typeof PaymentWebhookRequestSchema>;
export type PaymentWebhookResponse = z.infer<
  typeof PaymentWebhookResponseSchema
>;

// =============================================================================
// JS Code Validation
// =============================================================================


// Audit Trail Types
